import { SCAttributes } from "../../common/generic/generic-sc-attributes";
import { SCKpis } from "../../common/generic/generic-sc-domain";
import { IGenericAggregateResponse } from "../../common/generic/generic-types";
import { SCProfileDictionary } from "../../common/salescredit/salescredit-profile-dictionary";
import { ISCUser } from "../../common/salescredit/salescredit-role-types";
import { Format } from "../../common/utils/format";
import { ITreeMapSalesBySector } from "./types";

interface ILevelSelector {
  ytd: number;
}

interface ILevel3Sector {
  name: string;
  ytd: number;
  ytd1: number;
  yoyper: number;
  yvy1: number;
}

interface ILevel2Sector {
  name: string;
  ytd: number;
  ytd1: number;
  level3Sectors: ILevel3Sector[];
}

function calculateColor(yoyPer: number) {
  if (yoyPer < -1) {
    return "#dc4941";
  }
  if (yoyPer < -0.75) {
    return "#e36c67";
  }
  if (yoyPer < -0.5) {
    return "#ea918d";
  }
  if (yoyPer < -0.25) {
    return "#f1b5b3";
  }
  if (yoyPer < 0) {
    return "#f8dad9";
  }
  if (yoyPer < 0.25) {
    return "#d9f3eb";
  }
  if (yoyPer < 0.5) {
    return "#b3e7d7";
  }
  if (yoyPer < 0.75) {
    return "#8ddbc4";
  }
  if (yoyPer < 1) {
    return "#67cfb0";
  }
  if (yoyPer >= 1) {
    return "#41c49d";
  }
  return "#444852";
}

export function mapData(
  data: IGenericAggregateResponse,
  userRole: ISCUser
): ITreeMapSalesBySector {
  const requestYear = Format.GetYear(data.request ? data.request.date : "");
  const mappedData: ITreeMapSalesBySector = {
    Data: [],
    DrillDownData: [],
    ValidData: true,
    Y1: requestYear,
    Y2: requestYear - 1,
  };

  let totalYtd = 0;
  let totalYtd1 = 0;
  const pd = new SCProfileDictionary();
  // All Data is Keyed on LV2 Name. This builds an object to hold the each L2 record with
  // a collection of the L3 records inside
  const combinedData: {
    [key: string]: {
      ytd: number;
      ytd1: number;
      level3: {
        [key: string]: {
          ytd: number;
          ytd1: number;
          yoyPer: number;
          yvy1: number;
        };
      };
    };
  } = {};

  for (const row of data.aggregates ? data.aggregates : []) {
    // Get the Values
    const yoyPer =
      row.indicatorKpiValues[pd.GetKpi(userRole.Profile, SCKpis.SC_YOY_PER)];
    const ytd =
      row.indicatorKpiValues[pd.GetKpi(userRole.Profile, SCKpis.SC_YTD)];
    const ytd1 =
      row.indicatorKpiValues[pd.GetKpi(userRole.Profile, SCKpis.SC_YTD_1)];
    const yvy1 =
      row.indicatorKpiValues[pd.GetKpi(userRole.Profile, SCKpis.SC_Y_VS_Y1)];
    const lv2Name =
      row.dimensionAttributeValues[SCAttributes.SC_CLIENT_LE_TYPEL2_NAME];
    const lv3Name =
      row.dimensionAttributeValues[SCAttributes.SC_CLIENT_LE_TYPEL3_NAME];

    // Add the Dictionary ROW if it doesn't exist
    AddRow(lv2Name, lv3Name, combinedData);

    // ADD YTD if not null
    if (!isNaN(ytd)) {
      totalYtd += ytd;
      combinedData[lv2Name].ytd += ytd;
      combinedData[lv2Name].level3[lv3Name].ytd += ytd;
    }
    // ADD YTD-1 if not null
    if (!isNaN(ytd1)) {
      totalYtd1 += ytd1;
      combinedData[lv2Name].ytd1 += ytd1;
      combinedData[lv2Name].level3[lv3Name].ytd1 += ytd1;
    }
    // ADD YOY-Per if not null
    if (!isNaN(yoyPer)) {
      combinedData[lv2Name].level3[lv3Name].yoyPer += yoyPer;
    }
    // ADD Y v Y-1 if not null
    if (!isNaN(yoyPer)) {
      combinedData[lv2Name].level3[lv3Name].yvy1 += yvy1;
    }
  }

  if (totalYtd === 0) {
    mappedData.ValidData = false;
    return mappedData;
  }

  // Sorts the Keyed Data by YTD (Both L2 & L3 Levels) and puts everythings in arrays
  const sortedData = MapToL2Sectors(combinedData);
  // Loops through the data calculates the fields and Combines anything over 5 record into others
  const groupedData = CalculateData(
    sortedData,
    totalYtd,
    totalYtd1,
    requestYear
  );
  mappedData.Data = groupedData[0];
  mappedData.DrillDownData = groupedData[1];
  return mappedData;
}

function CalculateYoyPercentage(ytd: number, ytd1: number) {
  if (ytd1 === 0 && ytd > 0) {
    return 1;
  }
  if (ytd === 0 && ytd1 > 0) {
    return -1;
  }
  if (ytd === 0 && ytd1 === 0) {
    return 0;
  }
  return (ytd - ytd1) / ytd1;
}

// tslint:disable-next-line:cognitive-complexity
function CalculateData(
  sortedSectorData: ILevel2Sector[],
  ytdtotal: number,
  ytd1total: number,
  y1: number
): any[] {
  let rowCount = 0;
  const lv2Sectors = [];
  const drillDownData = [];
  let otherYtdTotal = 0;
  let otherYtd1Total = 0;

  for (const l2Sector of sortedSectorData) {
    const yoy = CalculateYoyPercentage(l2Sector.ytd, l2Sector.ytd1);
    const drillSeriesData = [];
    if (rowCount <= 4) {
      lv2Sectors.push({
        color: calculateColor(yoy),
        drilldown: l2Sector.name,
        name: l2Sector.name,
        percentage:
          Format.FormatNumber((l2Sector.ytd / ytdtotal) * 100, 1, 0) + "%",
        sc_yor_per: Format.FormatYoY(yoy, true),
        sc_ytd_1: Format.FormatNumber(l2Sector.ytd1, 1000, 0),
        value: Format.FormatNumber(l2Sector.ytd, 1000, 0),
        y1,
        y2: y1 - 1,
      });
      let level3OtherYtdTotal = 0;
      let level3OtherYtd1Total = 0;
      let l3rowCount = 0;
      for (const l3Sector of l2Sector.level3Sectors) {
        if (l3rowCount <= 4) {
          drillSeriesData.push({
            color: calculateColor(l3Sector.yoyper),
            name: l3Sector.name,
            percentage:
              Format.FormatNumber((l3Sector.ytd / l2Sector.ytd) * 100, 1, 0) +
              "%",
            sc_yor_per: Format.FormatYoY(l3Sector.yoyper, true),
            sc_ytd_1: Format.FormatNumber(l3Sector.ytd1, 1000, 0),
            value: Format.FormatNumber(l3Sector.ytd, 1000, 0),
            y1,
            y2: y1 - 1,
          });
        } else {
          level3OtherYtdTotal += l3Sector.ytd;
          level3OtherYtd1Total += l3Sector.ytd1;
        }
        l3rowCount++;
      }
      if (l3rowCount > 4) {
        const yoyl3Other = CalculateYoyPercentage(
          level3OtherYtdTotal,
          level3OtherYtd1Total
        );
        drillSeriesData.push({
          color: calculateColor(yoyl3Other),
          name: "Other",
          percentage:
            Format.FormatNumber(
              (level3OtherYtdTotal / l2Sector.ytd) * 100,
              1,
              0
            ) + "%",
          sc_yor_per: Format.FormatYoY(yoyl3Other, true),
          sc_ytd_1: Format.FormatNumber(level3OtherYtd1Total, 1000, 0),
          value: Format.FormatNumber(level3OtherYtdTotal, 1000, 0),
          y1,
          y2: y1 - 1,
        });
      }
      drillDownData.push({
        data: drillSeriesData,
        id: l2Sector.name,
        name: l2Sector.name,
      });
    } else {
      otherYtdTotal += l2Sector.ytd;
      otherYtd1Total += l2Sector.ytd1;
    }
    rowCount++;
  }

  if (rowCount > 4) {
    const yoyl2Other = CalculateYoyPercentage(otherYtdTotal, otherYtd1Total);
    lv2Sectors.push({
      color: calculateColor(yoyl2Other),
      drilldown: null,
      name: "Others",
      percentage:
        Format.FormatNumber((otherYtdTotal / ytdtotal) * 100, 1, 0) + "%",
      sc_yor_per: Format.FormatYoY(yoyl2Other, true),
      sc_ytd_1: Format.FormatNumber(otherYtd1Total, 1000, 0),
      value: Format.FormatNumber(otherYtdTotal, 1000, 0),
      y1,
      y2: y1 - 1,
    });
  }
  return [lv2Sectors, drillDownData];
}

function AddRow(
  level2Name: string,
  level3Name: string,
  currentData: {
    [key: string]: {
      ytd: number;
      ytd1: number;
      level3: {
        [key: string]: {
          ytd: number;
          ytd1: number;
          yoyPer: number;
          yvy1: number;
        };
      };
    };
  }
) {
  if (!(level2Name in currentData)) {
    currentData[level2Name] = {
      level3: {},
      ytd: 0,
      ytd1: 0,
    };
    currentData[level2Name].level3[level3Name] = {
      yoyPer: 0,
      ytd: 0,
      ytd1: 0,
      yvy1: 0,
    };
    return;
  }

  if (!(level3Name in currentData[level2Name].level3)) {
    currentData[level2Name].level3[level3Name] = {
      yoyPer: 0,
      ytd: 0,
      ytd1: 0,
      yvy1: 0,
    };
  }
}

function MapToL2Sectors(keyedData: {
  [key: string]: {
    ytd: number;
    ytd1: number;
    level3: {
      [key: string]: {
        ytd: number;
        ytd1: number;
        yoyPer: number;
        yvy1: number;
      };
    };
  };
}): ILevel2Sector[] {
  const newArray: ILevel2Sector[] = [];
  for (const key of Object.keys(keyedData)) {
    newArray.push({
      level3Sectors: MapToL3Sectors(keyedData[key].level3),
      name: key,
      ytd: keyedData[key].ytd,
      ytd1: keyedData[key].ytd1,
    });
  }
  newArray.sort(ytdComparator);
  return newArray;
}

function MapToL3Sectors(level3KeyedData: {
  [key: string]: {
    ytd: number;
    ytd1: number;
    yoyPer: number;
    yvy1: number;
  };
}): ILevel3Sector[] {
  const newArray: ILevel3Sector[] = [];

  for (const key of Object.keys(level3KeyedData)) {
    newArray.push({
      name: key,
      yoyper: level3KeyedData[key].yoyPer,
      ytd: level3KeyedData[key].ytd,
      ytd1: level3KeyedData[key].ytd1,
      yvy1: level3KeyedData[key].yvy1,
    });
  }
  newArray.sort(ytdComparator);
  return newArray;
}

function ytdComparator(leftSide: ILevelSelector, rightSide: ILevelSelector) {
  if (leftSide.ytd < rightSide.ytd) {
    return 1;
  }
  if (leftSide.ytd > rightSide.ytd) {
    return -1;
  }
  return 0;
}

export function BuildSalesBySectorTreeMap(mappedData: ITreeMapSalesBySector) {
  return {
    chart: {
      spacing: [1, 1, 1, 1],
      type: "treemap",
    },
    credits: {
      enabled: false,
    },
    drilldown: {
      enabledLabels: true,
      series: mappedData.DrillDownData,
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          format: "{point.name} {point.percentage}",
          style: {
            color: "#333",
            fontSize: "",
            fontWeight: "600",
            textOutline: "1px contrast",
          },
        },
      },
    },
    series: [
      {
        allowDrillToNode: true,
        borderColor: "#808080",
        data: mappedData.Data,
        dataLabels: {
          format: "{point.name}  {point.percentage}",
        },
        layoutAlgorithm: "squarified",
      },
    ],
    title: {
      text: "",
    },
    tooltip: {
      crosshairs: true,
      pointFormat: `
        <b>{point.name}</b>:
        <br/><b>{point.y1}</b>:      {point.value:,.0f} K€ ({point.sc_yor_per} YoY)<br/>
        <br/><b>{point.y2}</b>: {point.sc_ytd_1:,.0f} K€<br/>
      `,
    },
  };
}
